import { useEffect, useRef } from 'react';
import NetInfo from '@react-native-community/netinfo';
import { isOrganizationEntitlementsCacheFresh } from '../feature-flags/planEntitlementsService';
import { ensureFreshAccessToken } from '../../shared/supabase/supabaseClient';
import { captureCatch } from '../../shared/sentry/sentryUser';

export function useNetInfoReconnect(
  userId: string | undefined,
  refreshEntitlements: () => Promise<unknown>,
): void {
  const netInfoWasConnectedRef = useRef<boolean | null>(null);
  const netInfoReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const sub = NetInfo.addEventListener((state) => {
      if (!userId) {
        return;
      }
      const connected = Boolean(state.isConnected);
      const prev = netInfoWasConnectedRef.current;
      netInfoWasConnectedRef.current = connected;
      // Primera emisión: fijar baseline sin duplicar el fetch inicial de PlanEntitlements.
      if (prev === null) {
        return;
      }
      if (connected && prev === false) {
        if (netInfoReconnectTimerRef.current) {
          clearTimeout(netInfoReconnectTimerRef.current);
        }
        netInfoReconnectTimerRef.current = setTimeout(() => {
          netInfoReconnectTimerRef.current = null;
          if (!isOrganizationEntitlementsCacheFresh()) {
            void refreshEntitlements();
          }
          void ensureFreshAccessToken().catch(captureCatch('shell', 'netinfo-reconnect-token-refresh'));
        }, 800);
      }
    });
    return () => {
      sub();
      if (netInfoReconnectTimerRef.current) {
        clearTimeout(netInfoReconnectTimerRef.current);
        netInfoReconnectTimerRef.current = null;
      }
    };
  }, [userId, refreshEntitlements]);

  useEffect(() => {
    netInfoWasConnectedRef.current = null;
  }, [userId]);
}
